/**
 * The arithmetic behind the viewer's zoom and pan, with no DOM in it.
 *
 * Separated from `ZoomStage` because none of it can be tested through jsdom:
 * jsdom lays nothing out, so every rect is 0×0 and every element reports a
 * `naturalWidth` of 0. Pulled out here, the rules that actually decide what a
 * reviewer sees — how far a zoom may go, where the anchor stays, how far a pan
 * may travel — are ordinary functions with ordinary tests.
 *
 * **The model, once, because every function below assumes it.** The image is
 * laid out fitted and centred by CSS, and the transform is applied on top with
 * the origin at its centre:
 *
 * ```
 * transform: translate(x, y) scale(s)
 * ```
 *
 * so `x` and `y` are stage pixels measured from the centre, and a point sitting
 * `c` pixels from the image's centre lands at `x + s · c`. Centre-origin rather
 * than the more usual `0 0` because it makes the pan limit symmetric — the
 * slack is the overflow halved, on each side — and because it means "fit" is
 * exactly `{ scale: 1, x: 0, y: 0 }` with nothing to compute.
 */

export interface Transform {
  scale: number;
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

/** Stage pixels measured from the stage's centre — the same space as `x`/`y`. */
export interface Point {
  x: number;
  y: number;
}

/** The lowest ceiling worth offering, and the fallback when nothing is known. */
const FALLBACK_MAX_SCALE = 4;
const MINIMUM_MAX_SCALE = 2;

/**
 * `+ 0` normalises negative zero, which `Math.max(-0, …)` produces whenever the
 * slack on an axis is zero and the offset is negative. It is identity for every
 * other value. Left in place it would put `-0px` in a transform string —
 * harmless to render, and enough to make "this is exactly centred" fail an
 * equality assertion for a reason that has nothing to do with the picture.
 */
function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value)) + 0;
}

/**
 * Hold a zoom between fit and the ceiling.
 *
 * The floor is 1 and not lower: below fit the image would sit in a field of
 * empty stage, which is a way to make a photograph smaller and no way to read
 * one. Getting back to fit is what the reset control is for.
 */
export function clampScale(scale: number, maxScale: number): number {
  return clamp(scale, 1, maxScale);
}

/**
 * Change scale while keeping whatever is under `anchor` under `anchor`.
 *
 * `anchor` is in the same space as `x` and `y` — stage pixels from the centre —
 * so a wheel event contributes `event.clientX - (rect.left + rect.width / 2)`.
 *
 * Solving `anchor = x' + s'·c` for the `c` that `anchor = x + s·c` already
 * fixes gives the one line below. Without it a zoom walks toward the centre of
 * the stage, and a reviewer zooming into a licence plate watches it slide away.
 */
export function anchoredZoom(from: Transform, nextScale: number, anchor: Point): Transform {
  const ratio = nextScale / from.scale;

  return {
    scale: nextScale,
    x: anchor.x - (anchor.x - from.x) * ratio,
    y: anchor.y - (anchor.y - from.y) * ratio,
  };
}

/**
 * Keep the image covering the stage it overflows, and centred on the axis it
 * does not.
 *
 * The slack on an axis is however much the scaled image hangs past the stage,
 * halved — so at fit there is none and any offset collapses to centre, which is
 * what makes a stray drag at scale 1 a no-op rather than a way to push the
 * photograph off the screen.
 *
 * **Each axis is clamped against its own overflow.** A wide image zoomed in has
 * room to travel across and none at all down; borrowing one limit for the other
 * would open a band of empty stage above or below it.
 *
 * A stage of zero size is left alone: that is a frame that has not been
 * measured yet, and clamping against it would snap a live gesture to the centre
 * the moment a resize observer fired early.
 */
export function clampTranslate(transform: Transform, stage: Size, fitted: Size): Transform {
  if (stage.width <= 0 || stage.height <= 0) return transform;

  const slackX = Math.max(0, (fitted.width * transform.scale - stage.width) / 2);
  const slackY = Math.max(0, (fitted.height * transform.scale - stage.height) / 2);

  return {
    scale: transform.scale,
    x: clamp(transform.x, -slackX, slackX),
    y: clamp(transform.y, -slackY, slackY),
  };
}

/**
 * How far this particular image may be zoomed before magnification stops
 * revealing anything.
 *
 * **This function is the quality requirement.** A fixed ceiling — the reference
 * implementation this viewer was modelled on uses `4` — caps a 6000-pixel scene
 * photograph well below the detail it actually contains, which in an evidence
 * system means the file holds something the reader is not allowed to see. The
 * honest ceiling is the point at which one image pixel covers one **device**
 * pixel, which depends on the image, the size of the stage, and the display:
 * the same photograph goes twice as far on a Retina screen as on a 1× one.
 *
 * Two guards.
 *
 * **The fallback.** `naturalWidth` is 0 until the image decodes, and in jsdom
 * it is 0 forever — so without this every component test would run against a
 * ceiling of 0 and a stage that refuses to zoom.
 *
 * **The floor.** An image smaller than the stage is already past 1:1 at fit, so
 * its honest ceiling is below 1× — a viewer that cannot zoom at all. A
 * screenshot blown up twice is still readable, and a control that silently does
 * nothing is worse than a slightly soft one.
 */
export function nativeMaxScale(
  naturalWidth: number,
  renderedCssWidth: number,
  devicePixelRatio: number,
): number {
  if (naturalWidth <= 0 || renderedCssWidth <= 0) return FALLBACK_MAX_SCALE;

  return Math.max(MINIMUM_MAX_SCALE, (naturalWidth / renderedCssWidth) * devicePixelRatio);
}
